import { useState } from 'react'
import { ChevronUp, ChevronDown, Archive } from 'lucide-react'

export default function ArchiveTable({
  files,
  loading,
  selected,
  onSelect,
  checked,
  onToggle,
  onToggleAll,
}) {
  const [sortKey, setSortKey] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else if (sortDir === 'desc') { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  function sortIcon(key) {
    if (sortKey !== key) return null
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
  }

  const sorted = [...files]
  if (sortKey) {
    sorted.sort((a, b) => {
      let av, bv
      switch (sortKey) {
        case 'name':
          av = a.filename.toLowerCase()
          bv = b.filename.toLowerCase()
          break
        case 'size':
          av = a.size || 0
          bv = b.size || 0
          break
        case 'date':
          av = a.uploaded || ''
          bv = b.uploaded || ''
          break
        default:
          return 0
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  const allChecked = sorted.length > 0 && sorted.every(f => checked.has(f.filename))

  function formatBytes(bytes) {
    if (!bytes || bytes === 0) return '—'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  function formatDate(iso) {
    if (!iso) return '—'
    const d = new Date(iso)
    const now = new Date()
    const diff = now - d
    if (diff < 86400000) {
      const hrs = Math.floor(diff / 3600000)
      if (hrs < 1) return 'Just now'
      return `${hrs}h ago`
    }
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000)
      return `${days}d ago`
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="archive-table-wrap">
      <table className="archive-table">
        <thead>
          <tr>
            <th className="th-check">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={() => onToggleAll(sorted)}
              />
            </th>
            <th className="th-name sortable" onClick={() => handleSort('name')}>
              Filename {sortIcon('name')}
            </th>
            <th className="th-size sortable" onClick={() => handleSort('size')}>
              Size {sortIcon('size')}
            </th>
            <th className="th-date sortable" onClick={() => handleSort('date')}>
              Archived {sortIcon('date')}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 30 }}>Loading...</td></tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center', padding: 30, color: '#666' }}>
                <Archive size={20} style={{ opacity: 0.3, marginBottom: 8 }} /><br />
                No files found
              </td>
            </tr>
          ) : sorted.map(file => {
            const isActive = selected?.filename === file.filename
            const isChecked = checked.has(file.filename)

            return (
              <tr
                key={file.filename}
                className={`${isActive ? 'active' : ''} ${isChecked ? 'checked' : ''}`}
                onClick={() => onSelect(file)}
              >
                <td className="td-check" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(file.filename)}
                  />
                </td>
                <td className="td-name">{file.filename}</td>
                <td className="td-size">{formatBytes(file.size)}</td>
                <td className="td-date">{formatDate(file.uploaded)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
